/* eslint-disable prettier/prettier */
import { Typography } from '@mui/material'
import { Box } from '@mui/system'
import { DataGrid } from '@mui/x-data-grid'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import Container from '../../components/Container'
import ErrorBanner from '../../components/shared/ErrorBanner'
import InlineLoading from '../../components/shared/InlineLoading'
import PipelineService from '../../services/pipelineService'
import { SURFACE, TEXT } from '../../theme/tokens'

const columns = [
    {
        field: 'pipeline_name',
        headerName: 'Pipeline Name',
        width: 200,
        headerAlign: 'center',
    },
    {
        field: 'comm_type',
        headerName: 'Communication Type',
        width: 160,
        headerAlign: 'center',
        // History rows may carry comms as a legacy object or an array.
        valueGetter: (params) =>
            params.row.comms?.comm_type ??
            params.row.comms?.[0]?.comm_type ??
            '',
    },
    {
        field: 'status',
        headerName: 'Status',
        width: 160,
        headerAlign: 'center',
    },
    {
        field: 'created_by',
        headerName: 'Created By',
        width: 150,
        headerAlign: 'center',
    },
    {
        field: 'created_on',
        headerName: 'Date Created',
        width: 150,
        headerAlign: 'center',
        renderCell: (data) =>
            moment(data.row.created_on * 1000).format('DD-MM-YYYY'),
    },
]

// DataGrid is not covered by the MUI theme overrides, so it keeps a
// local sx — built from tokens rather than hardcoded colors.
const gridSx = {
    border: 'none',
    color: TEXT.secondary,
    // Headers in the overline slot, like the theme's table heads.
    '& .MuiDataGrid-columnHeaders': {
        background: SURFACE.sunken,
        borderBottom: `1px solid ${SURFACE.border}`,
        color: TEXT.muted,
        typography: 'overline',
    },
    '& .MuiDataGrid-cell': {
        borderBottom: `1px solid ${SURFACE.border}`,
        typography: 'body2',
    },
    '& .MuiDataGrid-row:hover': { background: SURFACE.cardHover },
    '& .MuiDataGrid-footerContainer': {
        borderTop: `1px solid ${SURFACE.border}`,
        color: TEXT.secondary,
    },
    '& .MuiTablePagination-root, & .MuiTablePagination-selectIcon, & .MuiSvgIcon-root':
        { color: TEXT.secondary },
    '& .MuiDataGrid-overlay': {
        background: 'transparent',
        color: TEXT.faint,
    },
    '& .MuiDataGrid-columnSeparator': { color: SURFACE.border },
}

const PipelineHistory = () => {
    const { pipelineName } = useParams()
    const dispatch = useDispatch()
    const { pipelineHistory } = useSelector((state) => state.pipeline)
    const [error, setError] = useState('')
    // Until the versions arrive the grid would say "No rows" (or, with the
    // store's last answer, show another pipeline's versions).
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        setError('')
        setLoading(true)
        dispatch(PipelineService.getPipelineHistory(pipelineName)).then(
            (resp) => {
                setLoading(false)
                if (resp.error) {
                    setError(
                        resp.payload?.error ?? 'Failed to load pipeline history'
                    )
                }
            }
        )
    }, [pipelineName])

    return (
        // "Deleted versions", not "History": an edit overwrites the pipeline in
        // place (PUT /comms/v2/pipeline/{id}), so only a delete leaves an
        // inactive version. A pipeline that was only edited showed "No rows"
        // under "History", which read as broken (end-to-end test, 2026-10-12).
        <Container title="Deleted versions">
            <Box sx={{ height: 500 }}>
                <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                        color: TEXT.primary,
                        mb: 0.5,
                    }}
                >
                    Deleted versions of {pipelineName}
                </Typography>
                <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: TEXT.muted, mb: 2 }}
                >
                    Edits are saved over the pipeline and are not kept as
                    versions. This lists the versions that were deleted.
                </Typography>
                {error && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}
                {/* The free DataGrid: the Pro grid was used only to pin one
                    column, and its hardcoded license key has expired, which
                    stamps "MUI X: License key expired" over the table. */}
                {loading ? (
                    <InlineLoading label="Loading deleted versions…" />
                ) : (
                    <DataGrid
                        columns={columns}
                        rows={pipelineHistory}
                        autoHeight
                        localeText={{ noRowsLabel: 'No deleted versions' }}
                        sx={gridSx}
                    />
                )}
            </Box>
        </Container>
    )
}

export default PipelineHistory
